import React, { useState, useEffect } from 'react';
import { Box, DialogActions, Grid, TextField, Typography, Button, FormControl, FormControlLabel, InputLabel, Select, MenuItem, LinearProgress, Switch, Autocomplete, Snackbar, Alert } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getData, postData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';

const CreateProject = ({ open, onClose, onSuccess }) => {
    const today = new Date();
    const [newProject, setNewProject] = useState({
        name: '',
        client: '',
        projectManager: null,
        teamLead: null,
        startDate: null,
        endDate: null,
        status: 'Todo',
        description: '',
        priority: 'Medium',
        progress: 0,
        manager_id: null,
        team_leader_id: null,
        enableTwoLevelTimesheetApproval: false,
        managerCanEdit: true,
        externalCanEdit: false,
        managerAutoApproveTimesheet: false,
        externalApproverName: '',
    });

    const [errors, setErrors] = useState({});
    const [teamLeadOptions, setTeamLeadOptions] = useState([]);
    const [managerOptions, setManagerOptions] = useState([]);
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const organizationId = userData?.organization_id;
    const [isLoading, setIsLoading] = useState(false);

    const [externalApproverOptions, setExternalApproverOptions] = useState([]);
    const [selectedExternalApprover, setSelectedExternalApprover] = useState(null);
    const [globalConfig, setGlobalConfig] = useState(null);

    useEffect(() => {
        const fetchExternalApprovers = async () => {
            const raw = localStorage.getItem('userData');
            const parsed = raw ? JSON.parse(raw) : null;
            const orgId = parsed?.organization_id;
            if (!orgId) return;
            try {
                const [approversRes, cfgRes] = await Promise.all([
                    getData(API_ENDPOINTS.getExternalApprovers(orgId)),
                    getData(API_ENDPOINTS.getTimesheetConfig()),
                ]);
                if (approversRes?.status === "SUCCESS") {
                    setExternalApproverOptions(
                        (approversRes.message?.users || []).map(u => ({
                            id: u.id,
                            name: `${u.first_name || ''} ${u.last_name || ''}`.trim(),
                        }))
                    );
                }
                const cfg = cfgRes?.result?.data ?? cfgRes?.result ?? null;
                setGlobalConfig(cfg);
            } catch {
                setExternalApproverOptions([]);
            }
        };
        fetchExternalApprovers();
    }, []);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const validate = () => {
        let tempErrors = {};
        if (!newProject.name.trim()) tempErrors.name = "Project Name is required";
        if (!newProject.client.trim()) tempErrors.client = "Client name is required";
        if (!newProject.manager_id) tempErrors.manager = "Please select a Project Manager";
        if (!newProject.team_leader_id) tempErrors.teamLead = "Please select a Team Lead";
        if (!newProject.startDate) tempErrors.startDate = "Start date is required";
        if (!newProject.endDate) tempErrors.endDate = "End date is required";

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    useEffect(() => {
        const fetchData = async () => {
            const userDataRaw = localStorage.getItem('userData');
            const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
            const organizationId = userData?.organization_id;

            if (!organizationId) return;

            try {
                const [teamLeadRes, managerRes] = await Promise.all([
                    getData(API_ENDPOINTS.fetchTeamLeadsOrManagers(organizationId, 'is_team_lead')),
                    getData(API_ENDPOINTS.fetchTeamLeadsOrManagers(organizationId, 'is_manager'))
                ]);

                if (teamLeadRes?.status === "SUCCESS") {
                    setTeamLeadOptions(teamLeadRes.message?.users?.map(u => ({
                        id: u.id,
                        name: `${u.first_name || ''} ${u.last_name || ''}`.trim()
                    })) || []);
                }

                if (managerRes?.status === "SUCCESS") {
                    setManagerOptions(managerRes.message?.users?.map(u => ({
                        id: u.id,
                        name: `${u.first_name || ''} ${u.last_name || ''}`.trim()
                    })) || []);
                }
            } catch (error) {
               return null;
            }
        };
        fetchData();
    }, []);

    const handleCreateProject = async () => {
        if (!validate()) {
            setSnackbar({ open: true, message: 'Please correct the highlighted errors.', severity: 'error' });
            return;
        }

        setIsLoading(true);
        try {
            const payload = {
                organization_id: organizationId,
                project_title: newProject.name,
                priority: newProject.priority,
                start_date: newProject.startDate.toISOString(),
                end_date: newProject.endDate.toISOString(),
                description: newProject.description,
                clients: [{ first_name: newProject.client, last_name: '' }],
                status: newProject.status,
                progress: newProject.progress || 0,
                manager_id: newProject.manager_id,
                team_leader_id: newProject.team_leader_id,
                // TODO: Backend API needs to accept these fields when timesheet approval config is implemented
                enable_two_level_timesheet_approval: newProject.enableTwoLevelTimesheetApproval,
                manager_can_edit_timesheet: newProject.managerCanEdit,
                external_can_edit_timesheet: newProject.externalCanEdit,
                manager_auto_approve_timesheet: newProject.managerAutoApproveTimesheet,
                external_approver_id: selectedExternalApprover?.id || null,
            };

            const response = await postData(API_ENDPOINTS.createNewProject, payload);
            if (response?.status === 'SUCCESS') {
                setSnackbar({ open: true, message: 'Project created successfully!', severity: 'success' });
                setTimeout(() => {
                    onSuccess();
                    handleCancelButton();
                }, 500);
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'Error creating project.', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelButton = () => {
        setNewProject({
            name: '', client: '', projectManager: null, teamLead: null,
            startDate: null, endDate: null, status: 'Todo', description: '',
            priority: 'Medium', progress: 0, manager_id: null, team_leader_id: null,
            enableTwoLevelTimesheetApproval: false,
            managerCanEdit: true,
            externalCanEdit: false,
            managerAutoApproveTimesheet: false,
            externalApproverName: '',
        });
        setSelectedExternalApprover(null);
        setErrors({});
        onClose();
    };

    return (
        <>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        label="Project Name"
                        fullWidth
                        required
                        size="small"
                        error={!!errors.name}
                        helperText={errors.name}
                        value={newProject.name}
                        onChange={(e) => {
                            const val = e.target.value;
                            const capitalizedValue = val.charAt(0).toUpperCase() + val.slice(1);
                            setNewProject({ ...newProject, name: capitalizedValue });
                        }}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        label="Client"
                        fullWidth
                        required
                        size="small"
                        error={!!errors.client}
                        helperText={errors.client}
                        value={newProject.client}
                        onChange={(e) => {
                            const val = e.target.value;
                            const capitalizedValue = val.charAt(0).toUpperCase() + val.slice(1);
                            setNewProject({ ...newProject, client: capitalizedValue })
                        }}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <Autocomplete
                        options={managerOptions}
                        getOptionLabel={(option) => option.name}
                        value={newProject.projectManager}
                        onChange={(e, val) => setNewProject({ ...newProject, projectManager: val, manager_id: val?.id || null })}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Project Manager"
                                required
                                size="small"
                                error={!!errors.manager}
                                helperText={errors.manager}
                            />
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <Autocomplete
                        options={teamLeadOptions}
                        getOptionLabel={(option) => option.name}
                        value={newProject.teamLead}
                        onChange={(e, val) => setNewProject({ ...newProject, teamLead: val, team_leader_id: val?.id || null })}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Team Lead"
                                required
                                size="small"
                                error={!!errors.teamLead}
                                helperText={errors.teamLead}
                            />
                        )}
                    />
                </Grid>

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <DatePicker
                            label="Start Date"
                            sx={{ width: '100%' }}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    required: true,
                                    error: !!errors.startDate,
                                    helperText: errors.startDate
                                }
                            }}
                            minDate={today}
                            value={newProject.startDate}
                            onChange={(date) => setNewProject({ ...newProject, startDate: date })}
                            format='dd/MM/yyyy'
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <DatePicker
                            label="End Date"
                            sx={{ width: '100%' }}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    required: true,
                                    error: !!errors.endDate,
                                    helperText: errors.endDate
                                }
                            }}
                            minDate={newProject.startDate || today}
                            value={newProject.endDate}
                            onChange={(date) => setNewProject({ ...newProject, endDate: date })}
                            format='dd/MM/yyyy'
                        />
                    </Grid>
                </LocalizationProvider>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Priority</InputLabel>
                        <Select
                            value={newProject.priority}
                            label="Priority"
                            onChange={(e) => setNewProject({ ...newProject, priority: e.target.value })}
                        >
                            <MenuItem value="Low">Low</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                {/* Timesheet Approval Settings — only shown when HR config scope is PROJECT AND two-level approval is enabled */}
                {globalConfig?.approval_scope === 'PROJECT' && globalConfig?.is_two_level_approval && (
                    <Grid size={{ xs: 12 }}>
                        <Box sx={{ borderRadius: 2, border: '1px solid #e0e7ff', bgcolor: '#f8f9ff', overflow: 'hidden' }}>
                            <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #e0e7ff', bgcolor: '#eef2ff' }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#4338ca' }}>
                                    Timesheet Approval Settings
                                </Typography>
                            </Box>
                            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {/* Two-Level Toggle */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>Enable Two-Level Approval</Typography>
                                        <Typography variant="caption" sx={{ color: '#64748b' }}>Requires Manager + External Approver sign-off</Typography>
                                    </Box>
                                    <Switch
                                        checked={newProject.enableTwoLevelTimesheetApproval}
                                        onChange={(e) => { setNewProject({ ...newProject, enableTwoLevelTimesheetApproval: e.target.checked, externalCanEdit: false }); if (!e.target.checked) setSelectedExternalApprover(null); }}
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#6366f1' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#6366f1' } }}
                                    />
                                </Box>

                                {/* External Approver — only when two-level */}
                                {newProject.enableTwoLevelTimesheetApproval && (
                                    <Autocomplete
                                        options={externalApproverOptions}
                                        getOptionLabel={(option) => option.name}
                                        value={selectedExternalApprover}
                                        onChange={(e, val) => setSelectedExternalApprover(val)}
                                        renderInput={(params) => (
                                            <TextField {...params} size="small" fullWidth label="External Approver" placeholder="Select external approver" />
                                        )}
                                    />
                                )}

                                {/* Manager Can Edit — fully manager-controlled at the project level in PROJECT scope */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                            Allow Manager to Edit Entries
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                                            Manager can modify hours and remarks before approving
                                        </Typography>
                                    </Box>
                                    <Switch
                                        checked={newProject.managerCanEdit}
                                        onChange={(e) => setNewProject({ ...newProject, managerCanEdit: e.target.checked })}
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#6366f1' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#6366f1' } }}
                                    />
                                </Box>

                                {/* External Can Edit — only when two-level; fully manager-controlled at the project level in PROJECT scope */}
                                {newProject.enableTwoLevelTimesheetApproval && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                                Allow External Approver to Edit Entries
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                External approver can modify limited fields
                                            </Typography>
                                        </Box>
                                        <Switch
                                            checked={newProject.externalCanEdit}
                                            onChange={(e) => setNewProject({ ...newProject, externalCanEdit: e.target.checked })}
                                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#6366f1' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#6366f1' } }}
                                        />
                                    </Box>
                                )}

                                {/* Auto-Approve Manager Review — only when two-level; skips this project's manager sign-off once the external approver clears it */}
                                {newProject.enableTwoLevelTimesheetApproval && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                                Auto-Approve Manager Review
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                Skip manager sign-off for this project once the external approver approves
                                            </Typography>
                                        </Box>
                                        <Switch
                                            checked={newProject.managerAutoApproveTimesheet}
                                            onChange={(e) => setNewProject({ ...newProject, managerAutoApproveTimesheet: e.target.checked })}
                                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#6366f1' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#6366f1' } }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Grid>
                )}

                <Grid size={{ xs: 12 }}>
                    <TextField
                        label="Description"
                        fullWidth
                        multiline
                        rows={2}
                        value={newProject.description}
                        onChange={(e) => {
                            const val = e.target.value;
                            const capitalizedValue = val.charAt(0).toUpperCase() + val.slice(1);
                            setNewProject({ ...newProject, description: capitalizedValue });
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <Box sx={{ p: 2, borderRadius: '12px', background: '#f8f9ff', border: '1px solid #e0e7ff' }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ color: '#3b82f6', fontWeight: 600 }}>
                            Project Progress ({newProject.progress}%)
                        </Typography>
                        <LinearProgress variant="determinate" value={Number(newProject.progress)} sx={{ height: 10, borderRadius: 5, my: 2 }} />
                        <TextField
                            type="number"
                            label="Progress %"
                            size="small"
                            value={newProject.progress}
                            onFocus={(e) => {
                                if (newProject.progress === 0) {
                                    setNewProject({ ...newProject, progress: '' });
                                }
                            }}
                            onBlur={(e) => {
                                if (newProject.progress === '') {
                                    setNewProject({ ...newProject, progress: 0 });
                                }
                            }}
                            onChange={(e) => {
                                const val = e.target.value;
                                const parsedVal = val === '' ? '' : Math.min(100, Math.max(0, parseInt(val) || 0));
                                setNewProject({ ...newProject, progress: parsedVal });
                            }}
                            fullWidth
                        />
                    </Box>
                </Grid>
            </Grid>

            <DialogActions>
                <Button onClick={handleCancelButton} variant="outlined" disabled={isLoading} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button onClick={handleCreateProject} variant="contained" disabled={isLoading} sx={{ textTransform: 'none' }}>
                    {isLoading ? 'Processing...' : 'Create Project'}
                </Button>
            </DialogActions>

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} sx={{ width: '100%' }}> {snackbar.message} </Alert>
            </Snackbar>
        </>
    );
};

export default CreateProject;
